import { useState } from 'react'
import type { ReactNode } from 'react'
import toast from 'react-hot-toast'
import {
  estimateMissionRhum,
  tradeCargoCap,
  useLaunchSmuggle,
  useLaunchBlackMarket,
  useSmuggleEstimate,
  useBlackMarketEstimate,
  useMyFleet,
  useMyMissions,
  ongoingMissionTo,
  type MapIslandDto,
} from '@/api/hooks'
import { dangerTier } from '@/lib/danger'
import { ResourceTally } from '@/components/game/ui'
import {
  WoodIcon, RumIcon, SkullIcon,
} from '@/components/game/GameIcons'
import { toggleShipSelection, selectManyShips } from '@/components/game/map/fleetSelection'
import {
  RationsLabel,
  CargoInput,
  ReputBreakdown,
  AmbushThreatCard,
  ShipPickerList,
} from '@/components/game/map/launcherBits'
import { cargoMeta } from '@/components/game/map/shipPicker/ShipPickerRow'
import { CARGO_SORTS } from '@/components/game/map/shipPicker/useShipFilter'
import { missionFleetCap } from '@/lib/fleetGroups'
import { tripDurationSeconds, TripDuration, convoySpeedPerHour, ConvoyPace } from '@/components/game/map/tripDuration'

/**
 * Outlaw panel at a pirate lair — the mirror of `TradeSection` for legal
 * ports. The lair offers BOTH directions: Contrebande (SELL your cargo to the
 * fence → gold, {@link SmuggleLauncher}) and Marché noir (BUY rhum/bois WITH gold
 * from the fence, {@link BlackMarketLauncher}). A Contrebande/Marché noir toggle
 * picks the side; both carry the same seeded interception risk.
 */
export function OutlawSection({ island }: { island: MapIslandDto }) {
  const [mode, setMode] = useState<'sell' | 'buy'>('sell')
  const cell = (active: boolean) =>
    `flex-1 px-2 py-1 rounded-md transition-colors ${active ? 'bg-purple-300/15 text-purple-200 font-semibold' : 'text-ocean-300 hover:text-purple-300'}`

  return (
    <div className="space-y-2">
      <div className="flex gap-1 rounded-lg border border-purple-400/20 bg-ocean-900/50 p-1 text-xs" role="group" aria-label="Sens du marché noir">
        <button type="button" onClick={() => setMode('sell')} aria-pressed={mode === 'sell'} className={cell(mode === 'sell')}>Contrebande</button>
        <button type="button" onClick={() => setMode('buy')} aria-pressed={mode === 'buy'} className={cell(mode === 'buy')}>Marché noir</button>
      </div>
      {mode === 'buy'
        ? <BlackMarketLauncher island={island} />
        : <SmuggleLauncher island={island} />}
    </div>
  )
}

/** The two goods always on offer at a marché noir (a lair has no `buyOptions`). */
const BLACK_MARKET_GOODS: { resource: 'wood' | 'rhum'; label: string; Icon: (p: { className?: string }) => ReactNode }[] = [
  { resource: 'wood', label: 'Bois', Icon: WoodIcon },
  { resource: 'rhum', label: 'Rhum', Icon: RumIcon },
]

/**
 * Marché noir launcher (Outlaw Lot 4): the BUY-side mirror of {@link SmuggleLauncher}.
 * A convoy sails GOLD to a pirate lair to BUY rhum or bois, at the same seeded
 * interception risk as a sell run. The exact gold cost is recomputed SERVER-SIDE
 * (rep-driven outlaw discount → never mirrored in the front), so the panel warns
 * about the risk + bounds the volume by the pooled hold, then debits at launch.
 */
export function BlackMarketLauncher({ island }: { island: MapIslandDto }) {
  const { data: fleet } = useMyFleet()
  const { data: missionsData } = useMyMissions()
  const launch = useLaunchBlackMarket()
  // One smuggling run at a time per fence — covers the black market AND the
  // contraband sell (same mission type toward the lair, 409).
  const ongoing = ongoingMissionTo(missionsData?.missions ?? [], island.id, 'smuggling')
  const dockedShips = fleet?.ships.filter((s) => s.status === 'docked') ?? []
  const fleetCap = missionFleetCap(fleet?.shipyard?.level)
  const resources = fleet?.resources
  const origin = fleet?.island

  // Convoy selection (ordered, like the raid) + the bought good + amount.
  const [selectedIds, setSelectedIds] = useState<number[]>([])
  const [resource, setResource] = useState<'wood' | 'rhum'>('wood')
  const [amount, setAmount] = useState(0)

  const selected = selectedIds
    .map((id) => dockedShips.find((s) => s.id === id))
    .filter((s): s is NonNullable<typeof s> => Boolean(s))

  const toggleShip = (id: number) => {
    setSelectedIds((prev) =>
      toggleShipSelection(prev, id, dockedShips, fleetCap, () => toast.error(`Convoi au max (${fleetCap} navires).`)),
    )
  }

  const selectMany = (ids: number[]) => {
    setSelectedIds((prev) =>
      selectManyShips(prev, ids, dockedShips, fleetCap, () => toast.error(`Convoi au max (${fleetCap} navires).`)),
    )
  }

  // Pooled hold = Σ each ship's effective hold × the account's Forge cargo
  // multiplier — the exact cap the server enforces on the bought volume.
  const forgeMult = fleet?.cargoForgeMult ?? 1
  const capacity = tradeCargoCap(selected.reduce((acc, s) => acc + s.cargoCapacity, 0), forgeMult)
  const overflow = amount > capacity

  // Server-AUTHORITATIVE gold cost for this exact convoy + good + amount: the
  // outlaw discount is rep-driven and a lair carries no base price, so (unlike the
  // legal commerce buy) the front can't mirror it — it asks the server. Used to
  // PREVIEW the cost AND gate the launch on affordable gold; the launch recomputes
  // the debit regardless (never trust the client for the actual charge).
  const estimate = useBlackMarketEstimate(selectedIds, island.id, resource, amount, selectedIds.length > 0)
  const goldCost = estimate.data?.goldCost ?? 0
  const notEnoughGold = resources ? goldCost > resources.gold : false

  const danger = island.npcDanger ?? 1
  const tier = dangerTier(danger)
  const Glyph = resource === 'rhum' ? RumIcon : WoodIcon

  const oneWayDistance = origin ? Math.sqrt((island.x - origin.x) ** 2 + (island.y - origin.y) ** 2) : 0
  const slowestSpeed = convoySpeedPerHour(selected, fleet?.travelSpeedForgeMult ?? 1)
  const roundTripHours = slowestSpeed > 0 ? (oneWayDistance * 2) / slowestSpeed : 0
  const tripSeconds = origin ? tripDurationSeconds(slowestSpeed, origin, island) : 0
  const rhumEstimate = estimateMissionRhum(selected, Math.max(60, roundTripHours * 3600))
  // Marché noir pays GOLD for the goods; the crew rations come out of rhum.
  const notEnoughRhum = resources ? rhumEstimate > resources.rum : false

  // Free warehouse space is PER-RESOURCE — the surplus that doesn't fit on arrival
  // is lost at sea (warn, never block, like the legal buy).
  const stored = resources ? (resource === 'rhum' ? resources.rum : resources.wood) : 0
  const freeStorage = resources ? Math.max(0, resources.capacity - stored) : 0
  const overStorage = resources ? amount > freeStorage : false

  const selectResource = (next: 'wood' | 'rhum') => {
    setResource(next)
    setAmount(0)
  }
  const tab = (active: boolean) =>
    `flex-1 inline-flex items-center justify-center gap-1 px-2 py-1 rounded-md transition-colors ${active ? 'bg-purple-300/15 text-purple-200 font-semibold' : 'text-ocean-300 hover:text-purple-300'}`

  const canLaunch =
    selected.length > 0 &&
    resources !== undefined && resources !== null &&
    amount > 0 &&
    !overflow &&
    !notEnoughGold &&
    !notEnoughRhum &&
    ongoing == null &&
    !launch.isPending

  if (dockedShips.length === 0) {
    return (
      <div className="bg-purple-500/10 rounded-lg p-3 space-y-2 border border-purple-400/30">
        <p className="text-xs text-purple-200 italic">
          Aucun navire à quai pour ce convoi.
        </p>
      </div>
    )
  }

  return (
    <div className="bg-purple-500/10 rounded-lg p-3 space-y-3 border border-purple-400/30">
      <div className="flex items-start justify-between gap-2">
        <div className="space-y-1">
          <p className="text-xs uppercase tracking-wider flex items-center gap-1.5">
            <SkullIcon className="w-4 h-4 text-purple-300" /><span className="text-purple-200">Marché noir</span>
          </p>
          <p className="flex items-center gap-2">
            <span className={`inline-flex items-center px-1.5 py-0.5 rounded border text-[11px] font-semibold uppercase tracking-wider ${tier.badge}`}>
              {tier.label}
            </span>
            <span className="text-xs text-ocean-400 tabular-nums">danger {danger}/10</span>
          </p>
          <p className="text-[11px] text-ocean-300 leading-snug">
            Achète au noir : prix fixé au repaire (remise ∝ ta notoriété hors-la-loi × le danger, creusée par le
            Faux-fond du meilleur navire du convoi), réglé à l'embarquement.
          </p>
        </div>
        <p className="text-xs text-ocean-300 whitespace-nowrap">
          Cap convoi : <strong className="text-sand-300">{fleetCap}</strong>
        </p>
      </div>

      {/* Good to buy — both wood + rhum are always on offer at a lair. */}
      <div className="flex gap-1 rounded-lg border border-purple-400/20 bg-ocean-900/50 p-1 text-xs" role="group" aria-label="Ressource à acheter">
        {BLACK_MARKET_GOODS.map((g) => (
          <button
            key={g.resource}
            type="button"
            onClick={() => selectResource(g.resource)}
            aria-pressed={resource === g.resource}
            className={tab(resource === g.resource)}
          >
            <g.Icon className="w-3.5 h-3.5" /> {g.label}
          </button>
        ))}
      </div>

      {/* Convoy multi-select (mirrors the contrebande sell side). */}
      <ShipPickerList
        ships={dockedShips}
        selectedIds={selectedIds}
        onToggle={toggleShip}
        cap={fleetCap}
        onSelectMany={selectMany}
        onClearAll={() => setSelectedIds([])}
        meta={cargoMeta}
        sorts={CARGO_SORTS}
        fleetWord="convoi"
        accentClass="accent-purple-300"
      />

      {/* Amount shortcuts + input — bounded by the pooled hold. */}
      <div className="flex flex-wrap gap-1">
        <button
          type="button"
          onClick={() => setAmount(capacity)}
          disabled={capacity <= 0}
          className="text-[11px] px-2 py-1 rounded bg-ocean-900/70 border border-sand-500/20 hover:border-sand-300/40 transition-colors disabled:opacity-40"
        >
          Max ({capacity})
        </button>
        <button
          type="button"
          onClick={() => setAmount(0)}
          className="text-[11px] px-2 py-1 rounded bg-ocean-900/70 border border-sand-500/20 hover:border-sand-300/40 transition-colors"
        >
          Vider
        </button>
      </div>

      <CargoInput label="à acheter" Icon={Glyph} value={amount} max={capacity} error={overflow} onChange={setAmount} />

      <p className="text-xs text-ocean-200 flex items-center justify-between flex-wrap gap-1">
        <span className={overflow ? 'text-red-400' : ''}>Cale {amount}/{capacity}</span>
        {selected.length > 0 && <TripDuration seconds={tripSeconds} />}
      </p>
      <ConvoyPace ships={selected} tripSeconds={tripSeconds} />
      {/* Server-computed gold cost preview — mirrors the legal commerce buy's "Coût ≈"
          line, but the number comes from the server (rep-driven outlaw discount). Réglé
          à l'embarquement; turns red when the bank can't cover it. */}
      {selected.length > 0 && amount > 0 && (
        <p className="text-xs text-ocean-200 flex items-center gap-1 flex-wrap">
          <span className="inline-flex items-center gap-1">
            Coût ≈ <ResourceTally gold={goldCost} className={notEnoughGold ? 'text-red-400 font-semibold' : 'text-sand-300'} />
          </span>
          {estimate.isFetching && <span className="text-ocean-400">…</span>}
          {notEnoughGold && <span className="text-red-400">(or insuffisant en banque)</span>}
        </p>
      )}
      <p className="text-xs text-ocean-300 flex items-center gap-1 flex-wrap">
        <RationsLabel /> : <span className={`inline-flex items-center gap-1 ${notEnoughRhum ? 'text-red-400 font-semibold' : 'text-sand-300'}`}>≈ {rhumEstimate} <RumIcon className="w-3.5 h-3.5" /></span>
        {notEnoughRhum && <span className="text-red-400">(rhum insuffisant en banque)</span>}
      </p>
      {overStorage && amount > 0 && (
        <p className="text-[11px] text-rum-300 leading-snug">
          ⚠️ ~{freeStorage} de place en entrepôt. Au-delà, le surplus sera <strong>perdu en mer</strong> s'il ne rentre pas au retour.
        </p>
      )}

      {/* Marine-blockade threat card — parité with the contrebande sell side + the
          commerce ambush: detection risk, the patrol the gold sailed would draw, and
          the selected convoy's win odds. Shown once a convoy + amount arm the estimate. */}
      {estimate.data !== undefined && !estimate.isFetching && amount > 0 && estimate.data.interceptionRisk > 0 && (
        <AmbushThreatCard
          risk={estimate.data.interceptionRisk}
          threat={estimate.data.interceptionThreat}
          fleet={estimate.data.interceptionFleet}
          title="Menace d'interception"
          tooltipLabel="Comment fonctionne une interception ?"
          tooltip={
            <>
              <b className="text-sand-100">Patrouille de la Marine.</b> Le risque monte avec ta notoriété
              hors-la-loi et le danger de la cible ; la force du blocus avec la valeur de ta cargaison.
              Repoussée, tu <b className="text-sand-100">forces le blocus</b> et récupères ta marchandise ;
              vaincue, l'or dépensé est perdu et un navire peut couler. Une escorte de combat améliore tes chances.
            </>
          }
        />
      )}

      {ongoing && (
        <p className="text-xs text-sand-300 flex items-start gap-1.5">
          <SkullIcon className="w-4 h-4 shrink-0 mt-0.5 animate-pulse" />
          <span>
            Une contrebande est déjà en cours vers cette île
            {ongoing.shipName ? ` (${ongoing.shipName})` : ''}.
          </span>
        </p>
      )}

      <button
        onClick={() => {
          if (selected.length === 0) return
          const shipIds = selected.map((s) => s.id)
          launch.mutate(
            { shipIds, destinationIslandId: island.id, resource, amount },
            {
              onSuccess: () => {
                toast.success(`${selected.length} navire(s) au marché noir de ${island.name}.`)
                setSelectedIds([])
                setAmount(0)
              },
              onError: (err) => toast.error(err.message || 'Marché noir impossible'),
            },
          )
        }}
        disabled={!canLaunch}
        title={ongoing ? 'Une contrebande est déjà en cours vers cette île.' : undefined}
        className="w-full text-sm px-3 py-1.5 rounded-md bg-purple-500 text-ocean-50 font-semibold hover:bg-purple-600 transition-colors disabled:opacity-40 disabled:cursor-not-allowed inline-flex items-center justify-center gap-1.5"
      >
        {ongoing
          ? 'Contrebande en cours'
          : launch.isPending
            ? 'Lancement…'
            : selected.length === 0
              ? 'Choisis un convoi'
              : amount <= 0
                ? 'Choisis une quantité'
                : overflow
                  ? 'Cale insuffisante'
                  : notEnoughGold
                    ? 'Or insuffisant'
                    : notEnoughRhum
                      ? 'Rhum insuffisant'
                      : <><SkullIcon className="w-4 h-4" /> Lancer le convoi ({selected.length})</>}
      </button>
    </div>
  )
}

export function SmuggleLauncher({ island }: { island: MapIslandDto }) {
  const { data: fleet } = useMyFleet()
  const { data: missionsData } = useMyMissions()
  const launch = useLaunchSmuggle()
  // One smuggling run at a time per fence (the backend rejects a second, 409).
  const ongoing = ongoingMissionTo(missionsData?.missions ?? [], island.id, 'smuggling')
  const dockedShips = fleet?.ships.filter((s) => s.status === 'docked') ?? []
  const fleetCap = missionFleetCap(fleet?.shipyard?.level)
  const resources = fleet?.resources
  const origin = fleet?.island

  // Convoy selection (ordered, like the raid) + pooled cargo.
  const [selectedIds, setSelectedIds] = useState<number[]>([])
  const [wood, setWood] = useState(0)
  const [rum, setRum] = useState(0)

  const selected = selectedIds
    .map((id) => dockedShips.find((s) => s.id === id))
    .filter((s): s is NonNullable<typeof s> => Boolean(s))

  const toggleShip = (id: number) => {
    setSelectedIds((prev) =>
      toggleShipSelection(prev, id, dockedShips, fleetCap, () => toast.error(`Convoi au max (${fleetCap} navires).`)),
    )
  }

  const selectMany = (ids: number[]) => {
    setSelectedIds((prev) =>
      selectManyShips(prev, ids, dockedShips, fleetCap, () => toast.error(`Convoi au max (${fleetCap} navires).`)),
    )
  }

  // Pooled hold = Σ each ship's effective hold (base × Cale renforcée) × the
  // account's Forge cargo multiplier — the exact cap the server enforces.
  const forgeMult = fleet?.cargoForgeMult ?? 1
  const capacity = tradeCargoCap(selected.reduce((acc, s) => acc + s.cargoCapacity, 0), forgeMult)
  const totalCargo = wood + rum
  const overflow = totalCargo > capacity
  const tooMuchWood = resources ? wood > resources.wood : false
  const tooMuchRum = resources ? rum > resources.rum : false

  const danger = island.npcDanger ?? 1
  const tier = dangerTier(danger)

  const oneWayDistance = origin ? Math.sqrt((island.x - origin.x) ** 2 + (island.y - origin.y) ** 2) : 0
  const slowestSpeed = convoySpeedPerHour(selected, fleet?.travelSpeedForgeMult ?? 1)
  const roundTripHours = slowestSpeed > 0 ? (oneWayDistance * 2) / slowestSpeed : 0
  const tripSeconds = origin ? tripDurationSeconds(slowestSpeed, origin, island) : 0
  const rhumEstimate = estimateMissionRhum(
    selected,
    Math.max(60, roundTripHours * 3600),
  )
  // Rations are paid from the bank on top of the rhum loaded as cargo.
  const notEnoughRhum = resources ? rhumEstimate + rum > resources.rum : false

  // Server-side interception risk + sale breakdown for this exact convoy + cargo.
  const estimate = useSmuggleEstimate(selectedIds, island.id, wood, rum, selectedIds.length > 0)

  const canLaunch =
    selected.length > 0 &&
    resources !== undefined && resources !== null &&
    totalCargo > 0 &&
    !overflow &&
    !tooMuchWood &&
    !tooMuchRum &&
    !notEnoughRhum &&
    ongoing == null &&
    !launch.isPending

  if (dockedShips.length === 0) {
    return (
      <div className="bg-purple-500/10 rounded-lg p-3 space-y-2 border border-purple-400/30">
        <p className="text-xs text-purple-200 italic">
          Aucun navire à quai pour ce convoi.
        </p>
      </div>
    )
  }

  return (
    <div className="bg-purple-500/10 rounded-lg p-3 space-y-3 border border-purple-400/30">
      <div className="flex items-start justify-between gap-2">
        <div className="space-y-1">
          <p className="text-xs uppercase tracking-wider flex items-center gap-1.5">
            <SkullIcon className="w-4 h-4 text-purple-300" /><span className="text-purple-200">Contrebande</span>
          </p>
          <p className="flex items-center gap-2">
            <span className={`inline-flex items-center px-1.5 py-0.5 rounded border text-[11px] font-semibold uppercase tracking-wider ${tier.badge}`}>
              {tier.label}
            </span>
            <span className="text-xs text-ocean-400 tabular-nums">danger {danger}/10</span>
          </p>
          <p className="text-[11px] text-ocean-300 leading-snug">
            Vends ta cargaison au noir (prime ∝ ta notoriété hors-la-loi × le danger).
          </p>
        </div>
        <p className="text-xs text-ocean-300 whitespace-nowrap">
          Cap convoi : <strong className="text-sand-300">{fleetCap}</strong>
        </p>
      </div>

      {/* Convoy multi-select (mirrors the raid). */}
      <ShipPickerList
        ships={dockedShips}
        selectedIds={selectedIds}
        onToggle={toggleShip}
        cap={fleetCap}
        onSelectMany={selectMany}
        onClearAll={() => setSelectedIds([])}
        meta={cargoMeta}
        sorts={CARGO_SORTS}
        fleetWord="convoi"
        accentClass="accent-purple-300"
      />

      {/* Cargo shortcuts + inputs (mirror the trade run). */}
      <div className="flex flex-wrap gap-1">
        {[
          { label: 'Max bois', apply: () => { setWood(Math.min(resources?.wood ?? 0, capacity)); setRum(0) } },
          { label: 'Max rhum', apply: () => { setRum(Math.min(resources?.rum ?? 0, capacity)); setWood(0) } },
          {
            label: '50/50',
            apply: () => {
              const half = Math.floor(capacity / 2)
              const w = Math.min(half, resources?.wood ?? 0)
              setWood(w)
              setRum(Math.min(capacity - w, resources?.rum ?? 0))
            },
          },
          { label: 'Vider', apply: () => { setWood(0); setRum(0) } },
        ].map((s) => (
          <button
            key={s.label}
            type="button"
            onClick={s.apply}
            disabled={capacity <= 0}
            className="text-[11px] px-2 py-1 rounded bg-ocean-900/70 border border-sand-500/20 hover:border-sand-300/40 transition-colors disabled:opacity-40"
          >
            {s.label}
          </button>
        ))}
      </div>

      <div className="grid grid-cols-2 gap-2">
        <CargoInput
          label="Bois"
          Icon={WoodIcon}
          value={wood}
          max={Math.min(resources?.wood ?? 0, capacity)}
          error={tooMuchWood}
          onChange={setWood}
        />
        <CargoInput
          label="Rhum"
          Icon={RumIcon}
          value={rum}
          max={Math.min(resources?.rum ?? 0, capacity)}
          error={tooMuchRum}
          onChange={setRum}
        />
      </div>

      <p className="text-xs text-ocean-200 flex items-center justify-between flex-wrap gap-1">
        <span className={overflow ? 'text-red-400' : ''}>Cale {totalCargo}/{capacity}</span>
        {selected.length > 0 && <TripDuration seconds={tripSeconds} />}
      </p>
      <ConvoyPace ships={selected} tripSeconds={tripSeconds} />
      <p className="text-xs text-ocean-300 flex items-center gap-1 flex-wrap">
        <RationsLabel /> : <span className={`inline-flex items-center gap-1 ${notEnoughRhum ? 'text-red-400 font-semibold' : 'text-sand-300'}`}>≈ {rhumEstimate} <RumIcon className="w-3.5 h-3.5" /></span>
        {notEnoughRhum && <span className="text-red-400">(rhum insuffisant en banque)</span>}
      </p>

      {/* Sale breakdown (réput) + Marine-blockade threat card — parité with the
          commerce launcher: the réput line, then the shared AmbushThreatCard
          (detection risk · force du blocus · composition). Shown once a convoy +
          cargo arm the estimate. */}
      {estimate.data !== undefined && !estimate.isFetching && totalCargo > 0 && (
        <>
          <ReputBreakdown
            base={estimate.data.breakdown.base}
            reputationBonus={estimate.data.breakdown.reputationBonus}
            moduleBonus={estimate.data.breakdown.moduleBonus}
            total={estimate.data.breakdown.total}
          />
          {/* Threat card only when an interception can actually fire (outlaw depth > 0):
              a loyal/neutral captain has 0 risk → hidden, exactly like the commerce
              ambush card. The réput breakdown above stays (it's risk-independent). */}
          {estimate.data.interceptionRisk > 0 && (
            <AmbushThreatCard
              risk={estimate.data.interceptionRisk}
              threat={estimate.data.interceptionThreat}
              fleet={estimate.data.interceptionFleet}
              title="Menace d'interception"
              tooltipLabel="Comment fonctionne une interception ?"
              tooltip={
                <>
                  <b className="text-sand-100">Patrouille de la Marine.</b> Le risque monte avec ta notoriété
                  hors-la-loi et le danger de la cible ; la force du blocus avec la valeur de ta cargaison.
                  Repoussée, tu <b className="text-sand-100">forces le blocus</b> et vends quand même ; vaincue,
                  cargaison confisquée et un navire peut couler. Une escorte de combat améliore tes chances.
                </>
              }
            />
          )}
        </>
      )}

      {ongoing && (
        <p className="text-xs text-sand-300 flex items-start gap-1.5">
          <SkullIcon className="w-4 h-4 shrink-0 mt-0.5 animate-pulse" />
          <span>
            Une contrebande est déjà en cours vers cette île
            {ongoing.shipName ? ` (${ongoing.shipName})` : ''}.
          </span>
        </p>
      )}

      <button
        onClick={() => {
          if (selected.length === 0) return
          const shipIds = selected.map((s) => s.id)
          launch.mutate(
            { shipIds, destinationIslandId: island.id, wood, rum },
            {
              onSuccess: () => {
                toast.success(`${selected.length} navire(s) en contrebande vers ${island.name}.`)
                setSelectedIds([])
                setWood(0)
                setRum(0)
              },
              onError: (err) => toast.error(err.message || 'Contrebande impossible'),
            },
          )
        }}
        disabled={!canLaunch}
        title={ongoing ? 'Une contrebande est déjà en cours vers cette île.' : undefined}
        className="w-full text-sm px-3 py-1.5 rounded-md bg-purple-500 text-ocean-50 font-semibold hover:bg-purple-600 transition-colors disabled:opacity-40 disabled:cursor-not-allowed inline-flex items-center justify-center gap-1.5"
      >
        {ongoing
          ? 'Contrebande en cours'
          : launch.isPending
            ? 'Lancement…'
            : selected.length === 0
              ? 'Choisis un convoi'
              : totalCargo === 0
                ? 'Charge la cale'
                : overflow
                  ? 'Cale insuffisante'
                  : notEnoughRhum
                    ? 'Rhum insuffisant'
                    : <><SkullIcon className="w-4 h-4" /> Lancer le convoi ({selected.length})</>}
      </button>
    </div>
  )
}
